import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "./use-auth";
import { useToast } from "./use-toast";
import { apiClient } from "@/lib/api-client";
import type { CompanyFinancialsResponse } from "@/api/generated/client/models";

const companyFinancialsKeys = {
    all: ["company-financials"] as const,
    report: (userId?: number) => [...companyFinancialsKeys.all, "report", userId ?? "anonymous"] as const,
};

export function useCompanyFinancials() {
    const { firebaseUser, user } = useAuth();
    const currentUserId = user?.id;

    return useQuery<CompanyFinancialsResponse>({
        queryKey: companyFinancialsKeys.report(currentUserId),
        // The report endpoint already excludes claims where this user's claim_access
        // blocks the financials module, so no per-claim filtering is needed here.
        queryFn: () => apiClient.companyFinancials.getCompanyFinancialsReport(),
        enabled: !!firebaseUser,
        staleTime: 60 * 1000,          // 1 min — financial data changes infrequently
        refetchOnWindowFocus: true,     // Refresh when user returns to tab
    });
}

/**
 * Hook for exporting the financials report as a styled Excel file.
 * Uses the backend worker-thread pipeline (same as comparison exports).
 */
export function useFinancialsExcelExport() {
    const { toast } = useToast();
    const mutation = useMutation({
        mutationKey: ["company-financials", "exportExcel"],
        mutationFn: () => apiClient.companyFinancials.exportCompanyFinancialsExcel(),
    });

    const handleExport = () => {
        mutation.mutate(undefined, {
            onSuccess: (blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = "financials.xlsx";
                document.body.appendChild(a);
                a.click();
                URL.revokeObjectURL(url);
                document.body.removeChild(a);

                toast({
                    title: "Export successful",
                    description: "Financials Excel report has been downloaded.",
                });
            },
            onError: (error) => {
                console.error("Financials Excel export error:", error);
                toast({
                    title: "Export failed",
                    description: error instanceof Error ? error.message : "Failed to export Excel",
                    variant: "destructive",
                });
            },
        });
    };

    return { handleExport, isPending: mutation.isPending };
}
